import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { includes } from '@nullvoxpopuli/ember-composable-helpers/helpers/includes';
import { all, task } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import add from 'ember-math-helpers/helpers/add';
import { eq, gt, notEq, or } from 'ember-truth-helpers';
import UiButton from 'garaje/components/ui-button';
import htmlSafe from 'garaje/helpers/html-safe';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import Invite from 'garaje/models/invite';
import type { BlocklistReport, BlocklistReportFilter } from 'garaje/models/screener-result';
import type ScreenerResult from 'garaje/models/screener-result';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import _countBy from 'lodash/countBy';
import { filter, mapBy, unique } from 'macro-decorators';

interface BlocklistSignature {
  Args: {
    result: ScreenerResult<BlocklistReport>;
    model: GuestWithVisitApproval;
  };
  Blocks: {
    default: [];
  };
}

export default class Blocklist extends Component<BlocklistSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare store: StoreService;

  @tracked blocklistFilters: BlacklistFilter[] = [];
  @tracked currentBlocklistFilterIndex = 0;

  /**
   * List of filter IDs that matched the current report.
   */
  @mapBy('args.result.report.matchedFilters', 'filterId') blocklistFilterIdsFromReports!: (string | null)[];

  @unique('blocklistFilterIdsFromReports') uniqueBlocklistFilterIdsFromReports!: (string | null)[];
  @filter('uniqueBlocklistFilterIdsFromReports', Boolean) blocklistFiltersIdsThatMatched!: string[];
  @mapBy('currentBlocklistFilterReports', 'keyword') currentBlocklistFilterMatchedValues!: (string | null)[];

  get invite(): Invite | undefined {
    return this.args.model instanceof Invite ? this.args.model : undefined;
  }

  get currentBlocklistFilterReports(): BlocklistReportFilter[] {
    const { matchedFilters } = this.args.result.report;
    return matchedFilters.filter(
      (report) =>
        report.filterId &&
        this.currentBlocklistFilter?.id &&
        Number(report.filterId) === Number(this.currentBlocklistFilter.id),
    );
  }

  get currentBlocklistFilter(): BlacklistFilter | undefined {
    return this.prioritizedBlocklistFilters[this.currentBlocklistFilterIndex];
  }

  get blocklistFilterReportCounts(): { [key: string]: number } {
    return _countBy(this.blocklistFilterIdsFromReports);
  }

  get prioritizedBlocklistFilters(): BlacklistFilter[] {
    // if filter A has more reports than filter B - then filter A comes first
    const count = this.blocklistFilterReportCounts;
    return this.blocklistFilters.toSorted((a, b) => (count[b.id] ?? 0) - (count[a.id] ?? 0));
  }

  get matchName(): string {
    return this.blocklistFilters[0]?.fullName || 'someone';
  }

  constructor(owner: unknown, args: BlocklistSignature['Args']) {
    super(owner, args);
    void this.loadBlocklistFiltersTask.perform();
  }

  loadBlocklistFiltersTask = task(async () => {
    try {
      const blocklistFilters = await all(
        this.blocklistFiltersIdsThatMatched.map((id) => this.store.findRecord('blacklist-filter', id)),
      );

      this.blocklistFilters = blocklistFilters;
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', 'Error', { details: parseErrorForDisplay(e) });
    }
  });

  previousMatch = (): void => {
    this.currentBlocklistFilterIndex--;
  };

  nextMatch = (): void => {
    this.currentBlocklistFilterIndex++;
  };

  <template>
    {{#if (gt this.blocklistFilters.length 1)}}
      <li class="text-sm mt-1 sm:mt-4">Visitor matches {{this.blocklistFilters.length}} people on your block list.</li>
    {{else if (eq this.blocklistFilters.length 1)}}
      <li class="text-sm mt-1 sm:mt-4">Visitor matches
        <span data-private>{{this.matchName}}</span>
        on your block list.</li>
    {{/if}}

    {{yield}}

    {{#if this.currentBlocklistFilter}}
      <div class="mt-4 border-t" data-test-blocklist-matches>
        {{#if (gt this.blocklistFilters.length 1)}}
          <div class="mt-4" data-test-approval-review-blocklist-pagination>
            <UiButton
              {{on "click" this.previousMatch}}
              data-test-approval-review-blocklist-pagination-back
              type="button"
              class="btn-reset -ml-2 px-4 py-2 align-text-bottom icon-arrow-left-gray"
              disabled={{eq this.currentBlocklistFilterIndex 0}}
            >
              <span class="sr-only">previous block list match</span>
            </UiButton>

            <span class="inline-block text-sm text-carbon-50" data-test-approval-review-blocklist-pagination-summary>
              {{add 1 this.currentBlocklistFilterIndex}}
              of
              {{this.blocklistFilters.length}}
            </span>

            <UiButton
              {{on "click" this.nextMatch}}
              data-test-approval-review-blocklist-pagination-forward
              type="button"
              class="btn-reset px-4 py-2 align-text-bottom icon-arrow-right-gray"
              disabled={{eq (add 1 this.currentBlocklistFilterIndex) this.blocklistFilters.length}}
            >
              <span class="sr-only">next block list match</span>
            </UiButton>
          </div>
        {{/if}}

        <div class="text-sm my-4" data-test-blocklist-match-summary>
          {{pluralize this.currentBlocklistFilterReports.length "match"}}
          to
          <span data-private>{{or this.currentBlocklistFilter.fullName "this blocked person"}}</span>
        </div>

        <dl class="text-xs text-carbon-50 mb-0 grid grid-cols-[auto_1fr] items-baseline gap-x-6 gap-y-2 sm:block">
          <dt class="sr-only">
            Photo
          </dt>
          <dd class="col-span-2 mb-2 sm:mb-0" data-private>
            {{#if this.currentBlocklistFilter.photo.original}}
              <a
                data-test-blocklist-filter-photo
                href={{this.currentBlocklistFilter.photo.original}}
                target="_blank"
                rel="noopener"
                class="block mt-1 mb-1 bg-center bg-cover"
                style={{htmlSafe
                  (concat
                    "width:120px; height:120px; background-image:url(" this.currentBlocklistFilter.photo.thumb ");"
                  )
                }}
              >
                <span class="sr-only">View full size photo</span>
              </a>
            {{else}}
              <div
                data-test-blocklist-filter-photo-default
                class="mt-1 mb-1 border bg-carbon-5 bg-center bg-no-repeat"
                style="width:80px; height:80px; background-image:url(/assets/images/profile.svg)"
              >
                <span class="sr-only">No photo available</span>
              </div>
            {{/if}}
          </dd>

          <dt class="text-xs text-carbon-50 font-bold uppercase sm:mt-4">Full name</dt>
          <dd class="break-words break-all text-right sm:text-left" data-test-blocklist-filter-name data-private>
            {{#let
              (includes this.currentBlocklistFilter.fullName this.currentBlocklistFilterMatchedValues)
              as |matched|
            }}
              <span
                class={{if matched "text-papaya-50 font-bold"}}
                data-test-blocklist-match={{if matched "true" "false"}}
              >{{or this.currentBlocklistFilter.fullName "n/a"}}</span>
            {{/let}}
          </dd>

          <dt class="text-xs text-carbon-50 font-bold uppercase sm:mt-2 sm:pt-1">Aliases</dt>
          <dd class="break-words break-all text-right sm:text-left" data-test-blocklist-filter-alias data-private>
            {{#each this.currentBlocklistFilter.aliases as |alias index|}}
              {{~if (notEq index 0) ","}}
              {{#let (includes alias this.currentBlocklistFilterMatchedValues) as |matched|~}}
                <span
                  class={{if matched "text-papaya-50 font-bold"}}
                  data-test-blocklist-match={{if matched "true" "false"}}
                >{{alias}}</span>
              {{~/let}}
            {{else}}
              n/a
            {{/each}}
          </dd>

          <dt class="text-xs text-carbon-50 font-bold uppercase sm:mt-2 sm:pt-1">Email addresses</dt>
          <dd class="break-words break-all text-right sm:text-left" data-test-blocklist-filter-email data-private>
            {{#each this.currentBlocklistFilter.emails as |email index|}}
              {{~if (notEq index 0) ","}}
              {{#let (includes email this.currentBlocklistFilterMatchedValues) as |matched|~}}
                <span
                  class={{if matched "text-papaya-50 font-bold"}}
                  data-test-blocklist-match={{if matched "true" "false"}}
                >{{email}}</span>
              {{~/let}}
            {{else}}
              n/a
            {{/each}}
          </dd>

          <dt class="text-xs text-carbon-50 font-bold uppercase sm:mt-2 sm:pt-1">Phone numbers</dt>
          <dd class="break-words break-all text-right sm:text-left" data-test-blocklist-filter-phone data-private>
            {{#each this.currentBlocklistFilter.phoneNumbers as |phone index|}}
              {{~if (notEq index 0) ","}}
              {{#let (includes phone this.currentBlocklistFilterMatchedValues) as |matched|~}}
                <span
                  class={{if matched "text-papaya-50 font-bold"}}
                  data-test-blocklist-match={{if matched "true" "false"}}
                >{{phone}}</span>
              {{~/let}}
            {{else}}
              n/a
            {{/each}}
          </dd>

          <dt class="text-xs text-carbon-50 font-bold uppercase sm:mt-2 sm:pt-1">Keywords</dt>
          <dd class="break-words break-all text-right sm:text-left" data-test-blocklist-filter-keywords>
            {{#each this.currentBlocklistFilter.otherKeywords as |keyword index|}}
              {{~if (notEq index 0) ","}}
              {{#let (includes keyword this.currentBlocklistFilterMatchedValues) as |matched|~}}
                <span
                  class={{if matched "text-papaya-50 font-bold"}}
                  data-test-blocklist-match={{if matched "true" "false"}}
                >{{keyword}}</span>
              {{~/let}}
            {{else}}
              n/a
            {{/each}}
          </dd>

          <dt class="text-xs text-carbon-50 font-bold uppercase sm:mt-2 sm:pt-1">Physical description</dt>
          <dd class="break-words break-all text-right sm:text-left" data-test-blocklist-filter-description>
            {{or this.currentBlocklistFilter.description "n/a"}}
          </dd>

          <dt class="text-xs text-carbon-50 font-bold uppercase sm:mt-2 sm:pt-1">Reason for blocking</dt>
          <dd class="break-words break-all text-right sm:text-left" data-test-blocklist-filter-reason>
            {{or this.currentBlocklistFilter.reason "n/a"}}
          </dd>
        </dl>
      </div>
    {{/if}}
  </template>
}
